import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

const useRecipeStore = create(
  persist(
    (set, get) => ({
      recipes: [],
      favorites: [],
      currentRecipe: null,
      isLoading: false,
      error: null,
      filters: {
        category: 'all',
        difficulty: 'all',
        time: 'all',
        searchQuery: ''
      },

      fetchRecipes: async () => {
        set({ isLoading: true, error: null })
        try {
          const response = await fetch('/api/recipes')
          if (!response.ok) throw new Error('Failed to fetch recipes')

          const recipes = await response.json()
          set({
            recipes,
            isLoading: false,
            error: null
          })

          return recipes
        } catch (error) {
          set({
            error: error.message,
            isLoading: false
          })
          throw error
        }
      },

      fetchRecipeById: async (id) => {
        set({ isLoading: true, error: null })
        try {
          const response = await fetch(`/api/recipes/${id}`)
          if (!response.ok) throw new Error('Recipe not found')

          const recipe = await response.json()
          set({
            currentRecipe: recipe,
            isLoading: false,
            error: null
          })

          return recipe
        } catch (error) {
          set({
            error: error.message,
            isLoading: false
          })
          throw error
        }
      },

      createRecipe: async (recipeData) => {
        set({ isLoading: true, error: null })
        try {
          const response = await fetch('/api/recipes', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(recipeData),
          })

          if (!response.ok) throw new Error('Failed to create recipe')

          const newRecipe = await response.json()
          set(state => ({
            recipes: [newRecipe, ...state.recipes],
            isLoading: false,
            error: null
          }))

          return newRecipe
        } catch (error) {
          set({
            error: error.message,
            isLoading: false
          })
          throw error
        }
      },

      updateRecipe: async (id, updates) => {
        set({ isLoading: true, error: null })
        try {
          const response = await fetch(`/api/recipes/${id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updates),
          })

          if (!response.ok) throw new Error('Failed to update recipe')

          const updatedRecipe = await response.json()
          set(state => ({
            recipes: state.recipes.map(recipe =>
              recipe.id === id ? updatedRecipe : recipe
            ),
            currentRecipe: state.currentRecipe?.id === id ? updatedRecipe : state.currentRecipe,
            isLoading: false,
            error: null
          }))

          return updatedRecipe
        } catch (error) {
          set({
            error: error.message,
            isLoading: false
          })
          throw error
        }
      },

      deleteRecipe: async (id) => {
        set({ isLoading: true, error: null })
        try {
          const response = await fetch(`/api/recipes/${id}`, {
            method: 'DELETE',
          })

          if (!response.ok) throw new Error('Failed to delete recipe')

          set(state => ({
            recipes: state.recipes.filter(recipe => recipe.id !== id),
            favorites: state.favorites.filter(fav => fav !== id),
            isLoading: false,
            error: null
          }))

        } catch (error) {
          set({
            error: error.message,
            isLoading: false
          })
          throw error
        }
      },

      toggleFavorite: (recipeId) => {
        set(state => ({
          favorites: state.favorites.includes(recipeId)
            ? state.favorites.filter(id => id !== recipeId)
            : [...state.favorites, recipeId]
        }))
      },

      setFilters: (newFilters) => {
        set(state => ({
          filters: { ...state.filters, ...newFilters }
        }))
      },

      getFilteredRecipes: () => {
        const { recipes, filters } = get()

        return recipes.filter(recipe => {
          if (filters.searchQuery && !recipe.title.toLowerCase().includes(filters.searchQuery.toLowerCase())) {
            return false
          }

          if (filters.category !== 'all' && recipe.category !== filters.category) {
            return false
          }

          if (filters.difficulty !== 'all' && recipe.difficulty !== filters.difficulty) {
            return false
          }

          if (filters.time !== 'all') {
            const timeRanges = {
              'quick': [0, 30],
              'medium': [30, 60],
              'long': [60, Infinity]
            }
            const [min, max] = timeRanges[filters.time]
            if (recipe.cookTime < min || recipe.cookTime > max) {
              return false
            }
          }

          return true
        })
      },

      generateImages: async (recipeId) => {
        set({ isLoading: true, error: null })
        try {
          const response = await fetch(`/api/recipes/${recipeId}/generate-images`, {
            method: 'POST',
          })

          if (!response.ok) throw new Error('Failed to generate images')

          const result = await response.json()

          // Update recipe with generated images
          set(state => ({
            recipes: state.recipes.map(recipe =>
              recipe.id === recipeId
                ? { ...recipe, stepImages: result.images }
                : recipe
            ),
            currentRecipe: state.currentRecipe?.id === recipeId
              ? { ...state.currentRecipe, stepImages: result.images }
              : state.currentRecipe,
            isLoading: false,
            error: null
          }))

          return result
        } catch (error) {
          set({
            error: error.message,
            isLoading: false
          })
          throw error
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'recipes-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        favorites: state.favorites,
        filters: state.filters
      }),
    }
  )
)

export default useRecipeStore